import { fromBuffer } from 'file-type/browser';
import type { MimeTypeExtended } from 'interfaces/app';
import moment from 'moment';
import {
	MIME_TYPES_HEIC,
	MIME_TYPES_PDF,
	MIME_TYPES_REQUIRE_CONVERSION,
} from 'settings/filetypes';

export type ImageDetails = {
	orientation?: number;
	fullWidth?: number;
	fullHeight?: number;
	photodate?: string;
	thumbUrl?: string | null;
	fcx?: number;
	fcy?: number;
	fcw?: number;
	fch?: number;
};

export type ResolvedFileData = {
	file: File;
	fileType: MimeTypeExtended;
	date: number;
};

export type ImageDetailsOptions = {
	maxWidth: number;
	maxHeight: number;
	faceRecognition: boolean;
};

export async function detectImageDetails(
	file: File,
	options: ImageDetailsOptions,
	fileType?: MimeTypeExtended,
): Promise<ImageDetails> {
	if (fileType && MIME_TYPES_REQUIRE_CONVERSION.includes(fileType)) {
		return {};
	}

	const loadImage = (await import('services/load-image')).default;

	try {
		const { data } = await loadImage(
			file,
			{
				faceRecognition: options.faceRecognition,
				meta: true,
				orientation: false,
				parallel: 2,
			},
		);

		const orientation = data?.exif
			? data.exif[0x0112] as number
			: undefined;

		let fullWidth: number | undefined;
		let fullHeight: number | undefined;

		if (data) {
			/*
			 * EXIF orientation values 5-8 are the four rotated-by-90 variants
			 * that swap width and height. Values 1-4 keep the axes and anything
			 * outside 1-8 is invalid, so only swap within the 5-8 range.
			 */
			if (orientation && orientation >= 5 && orientation <= 8) {
				fullWidth = data.originalHeight;
				fullHeight = data.originalWidth;
			} else {
				fullWidth = data.originalWidth;
				fullHeight = data.originalHeight;
			}
		}

		if (fullWidth && fullHeight) {
			const scale = Math.max(
				1,
				fullWidth / options.maxWidth,
				fullHeight / options.maxHeight,
			);
			fullWidth = Math.round(fullWidth / scale);
			fullHeight = Math.round(fullHeight / scale);
		}

		let fch: number | undefined;
		let fcw: number | undefined;
		let fcx: number | undefined;
		let fcy: number | undefined;

		if (data?.faces?.boundingBox && fullWidth && fullHeight) {
			const { boundingBox } = data.faces;
			fcx = Math.round(boundingBox.x1 * fullWidth);
			fcy = Math.round(boundingBox.y1 * fullHeight);
			fcw = Math.round((boundingBox.x2 - boundingBox.x1) * fullWidth);
			fch = Math.round((boundingBox.y2 - boundingBox.y1) * fullHeight);
		}

		const photodate = (data?.exif && data.exif[0x9003])
			? moment(
				data.exif[0x9003] as string,
				'YYYY:MM:DD HH:mm:ss',
			).format('X')
			: undefined;

		return {
			orientation,
			fullWidth,
			fullHeight,
			photodate,
			thumbUrl: (
				// @ts-expect-error: `Thumbnail` is missing in the type definition
				data?.exif?.Thumbnail
					// @ts-expect-error: `Thumbnail` is missing in the type definition
					? data.exif.Thumbnail
					: null
			),
			fcx,
			fcy,
			fcw,
			fch,
		};
	} catch (error) {
		/*
		 * Image inspection is best-effort: a failure here must not block the
		 * upload, so we fall back to empty details. Report the error instead of
		 * swallowing it silently, mirroring services/load-image's Bugsnag usage.
		 */
		if (typeof window.glBugsnagClient !== 'undefined') {
			window.glBugsnagClient.notify(
				error instanceof Error
					? error
					: new Error('Failed to inspect image details'),
				(event) => {
					event.severity = 'warning';
				},
			);
		}

		return {};
	}
}

export function resolveFileType(
	file: File,
	acceptedFileTypes: MimeTypeExtended[],
): Promise<ResolvedFileData> {
	const buildResult = (fileType?: MimeTypeExtended): ResolvedFileData => {
		/*
		 * `file.type` is a plain string that may be empty when the browser cannot
		 * determine the type. Narrow it against the accepted list instead of
		 * casting, so the returned `fileType` is a genuine MimeTypeExtended.
		 */
		const detectedType = fileType ?? file.type;
		const acceptedType = acceptedFileTypes.find((type) => type === detectedType);

		if (!acceptedType) {
			throw new Error(`Filetype ${detectedType || 'unknown'} not allowed`);
		}

		return {
			file,
			fileType: acceptedType,
			date: file.lastModified,
		};
	};

	if (window.FileReader) {
		return new Promise((resolve, reject) => {
			/*
			 * Resolve with the magic-byte type when it is accepted, otherwise fall
			 * back to the browser-reported file.type before giving up. file-type
			 * cannot fingerprint text formats — an SVG with an `<?xml` prolog sniffs
			 * as application/xml, which is not an accepted type — so without this
			 * fallback such uploads were silently dropped. The pre-XState controller
			 * fell back the same way; #1581 dropped it, which broke SVG uploads.
			 */
			const settle = (detectedMime?: MimeTypeExtended) => {
				try {
					resolve(buildResult(detectedMime));
				} catch (detectedError) {
					if (detectedMime === undefined) {
						reject(detectedError);

						return;
					}

					try {
						resolve(buildResult());
					} catch {
						reject(detectedError);
					}
				}
			};

			const blob = file.slice(
				0,
				20,
			);
			const reader = new FileReader();

			reader.onloadend = (e) => {
				const result = e.target?.result;
				const ready = (
					e.target
					&& e.target.readyState === FileReader.DONE
					&& result
					&& typeof result !== 'string'
				);

				if (!ready) {
					settle();

					return;
				}

				const bytes = new Uint8Array(result as ArrayBuffer);

				fromBuffer(bytes)
					.then((res) => settle(res?.mime))
					.catch(() => settle());
			};

			reader.readAsArrayBuffer(blob);
		});
	}

	const lowerFileName = file.name.toLowerCase();
	const buildResultAsync = (fileType?: MimeTypeExtended) => Promise
		.resolve()
		.then(() => buildResult(fileType));

	if (lowerFileName.endsWith('.heic')) {
		return buildResultAsync(MIME_TYPES_HEIC[0]);
	}

	if (lowerFileName.endsWith('.pdf')) {
		return buildResultAsync(MIME_TYPES_PDF[0]);
	}

	return buildResultAsync();
}
